/* eslint-disable @typescript-eslint/no-explicit-any */
/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXAtomValue, useStateXAtom } from '@cloudio-saas/statex';
import { itemAtom, runtimeItemAtom } from 'designer/atoms';
import { useRef, useEffect } from 'react';
import { getManager } from './Manager';
import {
  BaseActionProps,
  CommonProps,
} from '../layout/Sidebar/panels/PropertiesView/CommonProperties';
import { Platform } from '../platform/Platform';
import {
  ActionItemTypeRegistry,
  ComponentItemType,
  ComponentItemTypeRegistry,
  TriggerItemTypeRegistry,
} from './Base.type';
import {
  ActionManager,
  BaseComponentItem,
  ComponentItemData,
  ComponentManager,
  DefaultExpressionValue,
  Item,
  ItemID,
  PropertyDefinitions,
  RequiredItem,
  RequiredItemData,
  TriggerManager,
} from './Component.type';
import { useStringExpression } from 'expression/useExpression';
import { isEmpty } from 'utils/isEmpty';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const emptyFunction = () => {};
const EMPTY_OBJECT = Object.freeze({});
const EMPTY_ARRAY = Object.freeze([]);

const baseActionProps: PropertyDefinitions<
  Omit<BaseActionProps, keyof CommonProps>
> = {
  disableCondition: {
    label: 'Execute Condition',
    type: 'expressionOnly',
    returnType: () => 'boolean',
    helpText:
      'This action will be executed only if this condition is either blank or returns true.',
  },
};

const baseActionManager = <K extends keyof ActionItemTypeRegistry>(
  platform: Platform,
  itemType: K,
): Omit<ActionManager<K>, 'props' | 'defaultValue' | 'action'> => {
  const _mgr: Omit<ActionManager<K>, 'props' | 'defaultValue' | 'action'> = {
    managerType: 'Action',
    platform,
    canHaveChildren: () => false,
    onReplace: (item: Item<K>) => item,
    updateMetaData: (_: Item<K>, data: Required<ActionItemTypeRegistry[K]>) =>
      data,
    isChildAllowed: () => false,
    isAllowedInsideParent: () => true,
    getDataSourceAliasProperty: () => undefined,
    isDataSourceAliasUsed: () => false,
    isItemIdUsed: () => false,
    getDataSourceProperty: () => undefined,
    validateProperty: () => null,
    onPropertyChange: <KK extends keyof Partial<ActionItemTypeRegistry[K]>>(
      property: KK,
      value: ActionItemTypeRegistry[K][KK],
    ) => {
      return {
        [property]: value,
      } as unknown as Partial<ActionItemTypeRegistry[K]>;
    },
    isPropertyVisible: () => true,
    type: itemType,
    name: itemType,
    getDisplayText: (item: RequiredItem<K>, expanded: boolean) => {
      const l = item.children.length;
      if (expanded && l) {
        return '';
      }
      return l ? `(${l} item${l > 1 ? 's' : ''})` : '';
    },
    onChildMoveIn: emptyFunction,
    onChildMoveOut: emptyFunction,
    onMove: ({ value }: { oldValue: Item<K>; value: Item<K> }) => value,
    onCopy: (value: ActionItemTypeRegistry[K]) => value,
    defaultChildren: EMPTY_ARRAY,
    replaceableWith: EMPTY_ARRAY,
    isItemIdReadOnly: () => false,
    icon: { iconName: 'paper-plane' },
    helpRenders: EMPTY_ARRAY,
    generateShortId: () => '',
  };

  return _mgr;
};

const baseTriggerManager = <K extends keyof TriggerItemTypeRegistry>(
  platform: Platform,
  itemType: K,
): Omit<TriggerManager<K>, 'props' | 'defaultValue' | 'trigger'> => {
  const _mgr: Omit<TriggerManager<K>, 'props' | 'defaultValue' | 'trigger'> = {
    managerType: 'Trigger',
    platform,
    canHaveChildren: () => false,
    onReplace: (item: Item<K>) => item,
    replaceableWith: EMPTY_ARRAY,
    updateMetaData: (_: Item<K>, data: Required<TriggerItemTypeRegistry[K]>) =>
      data,
    isChildAllowed: () => false,
    isAllowedInsideParent: () => true,
    getDataSourceAliasProperty: () => undefined,
    isDataSourceAliasUsed: () => false,
    isItemIdUsed: () => false,
    getDataSourceProperty: () => undefined,
    validateProperty: () => null,
    onPropertyChange: <KK extends keyof Partial<TriggerItemTypeRegistry[K]>>(
      property: KK,
      value: TriggerItemTypeRegistry[K][KK],
    ) => {
      return {
        [property]: value,
      } as unknown as Partial<TriggerItemTypeRegistry[K]>;
    },
    isPropertyVisible: () => true,
    type: itemType,
    name: itemType,
    getDisplayText: (item: RequiredItem<K>, expanded: boolean) => {
      const l = item.children.length;
      if (expanded && l) {
        return '';
      }
      return l ? `(${l} item${l > 1 ? 's' : ''})` : '';
    },
    onChildMoveIn: emptyFunction,
    onChildMoveOut: emptyFunction,
    onMove: ({ value }: { oldValue: Item<K>; value: Item<K> }) => value,
    onCopy: (value: TriggerItemTypeRegistry[K]) => value,
    defaultChildren: EMPTY_ARRAY,
    isItemIdReadOnly: () => false,
    icon: { iconName: 'play' },
    helpRenders: EMPTY_ARRAY,
    generateShortId: () => '',
  };

  return _mgr;
};

const baseManager = <K extends keyof ComponentItemTypeRegistry, R = never>(
  platform: Platform,
  itemType: K,
): Omit<
  ComponentManager<K, R>,
  | 'props'
  | 'defaultValue'
  | 'managerType'
  | 'Component'
  | 'DEVComponent'
  | 'excludeChildren'
  | 'allowClick'
  | 'category'
> => {
  const _mgr: Omit<
    ComponentManager<K, R>,
    | 'props'
    | 'defaultValue'
    | 'managerType'
    | 'Component'
    | 'DEVComponent'
    | 'excludeChildren'
    | 'allowClick'
    | 'category'
  > = {
    platform,
    layout: 'auto',
    canHaveChildren: () => false,
    replaceableWith: EMPTY_ARRAY,
    onReplace: (item: Item<K>) => item,
    showEmptyPlaceholderInDesignerView: true,
    updateMetaData: (_: BaseComponentItem, data: RequiredItemData<K>) => data,
    isChildAllowed: () => false,
    isAllowedInsideParent: () => true,
    getDataSourceAliasProperty: () => undefined,
    isDataSourceAliasUsed: () => false,
    isItemIdUsed: () => false,
    getDataSourceProperty: () => undefined,
    validateProperty: () => null,
    onPropertyChange: (property, value) => {
      return {
        [property]: value,
      } as unknown as Partial<ComponentItemData<K>>;
    },
    isPropertyVisible: () => true,
    type: itemType,
    name: itemType,
    getDisplayText: (item: RequiredItem<K>, expanded: boolean) => {
      const l = item.children?.length;
      if (expanded && l) {
        return '';
      }
      return l ? `(${l} item${l > 1 ? 's' : ''})` : '';
    },
    getWrapperProps: () => EMPTY_OBJECT as R,
    onChildMoveIn: emptyFunction,
    onChildMoveOut: emptyFunction,
    onMove: ({ value }: { oldValue: Item<K>; value: Item<K> }) => value,
    onCopy: (value: ComponentItemTypeRegistry[K]) => value,
    defaultChildren: EMPTY_ARRAY,
    getParentItemTypes: () => EMPTY_ARRAY,
    isItemIdReadOnly: () => false,
    icon: { iconName: 'desktop' },
    helpRenders: EMPTY_ARRAY,
    generateShortId: () => '',
  };

  return _mgr;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const baseComponentManager = <
  K extends keyof ComponentItemTypeRegistry,
  R = never,
>(
  platform: Platform,
  itemType: K,
): Omit<ComponentManager<K, R>, 'props' | 'defaultValue'> => {
  const mgr: Omit<ComponentManager<K, R>, 'props' | 'defaultValue'> = {
    ...baseManager<K, R>(platform, itemType),
    category: 'General',
    excludeChildren: false,
    devStyles: {},
    allowClick: false,
    managerType: 'Component',
  };

  return mgr;
};

function changedKeys<T extends Record<string, unknown>>(
  a: T,
  b: T,
): (keyof T)[] {
  const collect: (keyof T)[] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      collect.push(key);
    }
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      collect.push(key);
    }
  }
  return collect;
}

function useMetadataValue<T extends ComponentItemType>(
  appUid: string,
  pageId: ItemID,
  itemId: string,
  type: T,
): RequiredItemData<T> & { className: string } {
  const mgr = getManager(type) as unknown as ComponentManager<T>;
  if (!mgr) {
    throw Error(`Unknown type ${type}`);
  }
  const item = useStateXAtomValue(itemAtom, {
    params: { appUid, pageId, itemId },
  }) as BaseComponentItem;
  if (!item) {
    throw Error(`Missing ${type} with itemId ${itemId} under page ${pageId}`);
  }
  const [runtimeItem, setRuntimeItem] = useStateXAtom(runtimeItemAtom, {
    params: { appUid, pageId, itemId },
  });

  const { props } = item;
  const propsRef = useRef(props);
  useEffect(() => {
    if (props !== propsRef.current) {
      const keys = changedKeys(
        props as Record<string, unknown>,
        propsRef.current as Record<string, unknown>,
      ) as string[];
      if (keys.length) {
        const itm = { ...runtimeItem };
        keys.forEach((key) => {
          delete itm[key];
        });
        setRuntimeItem(itm);
      }
      propsRef.current = props;
    }
  }, [itemId, props, runtimeItem, setRuntimeItem]);

  const data: RequiredItemData<T> & { className: string } = (
    Object.keys(mgr.defaultValue) as (keyof RequiredItemData<T>)[]
  ).reduce(
    (obj, key) => {
      // @ts-ignore item.props[key]
      obj[key] = runtimeItem[key] ?? item.props[key] ?? mgr.defaultValue[key];
      return obj;
    },
    {
      className: `${item.type.toLowerCase()} i${item.itemId}`,
    } as unknown as RequiredItemData<T> & { className: string },
  );

  const exp = mgr.isList
    ? DefaultExpressionValue
    : data.cssClassName ?? DefaultExpressionValue;
  const cssClassNameResult = useStringExpression({
    exp,
    context: {
      appUid,
      pageId,
      itemId,
      property: 'cssClassName',
      data: {},
    },
  });
  if (!isEmpty(cssClassNameResult)) {
    data.className = `${data.className} ${cssClassNameResult.trim()}`;
  }

  return mgr.updateMetaData(item, data) as RequiredItemData<T> & {
    className: string;
  };
}

export {
  baseActionManager,
  baseActionProps,
  baseComponentManager,
  baseManager,
  baseTriggerManager,
  useMetadataValue,
};
